import { useEffect, useRef, useState } from "react";
import type { ApiClient, Customer } from "../api/client.js";

const QUICK_EMOJIS = ["😀", "😁", "😂", "🥹", "😊", "😉", "😍", "😘", "🤔", "😢", "😭", "😡", "👍", "👎", "🙏", "👏", "💪", "🎉", "❤️", "🔥", "✅", "👋", "🤝", "📌"] as const;

export function MessageComposer(props: {
  api: ApiClient;
  canAct: boolean;
  coreAvailable: boolean;
  conversationId: string;
  customer: Customer;
  hasConnectedSession: boolean;
  onSent: () => Promise<void>;
  onTextChange: (value: string) => void;
  text: string;
  syntheticSmokeMode?: boolean;
}): React.JSX.Element {
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [sending, setSending] = useState(false);
  const [emojiOpen, setEmojiOpen] = useState(false);
  const [attachment, setAttachment] = useState<File | null>(null);
  const [attachmentPreview, setAttachmentPreview] = useState<string | null>(null);
  const attachmentInputRef = useRef<HTMLInputElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const active = useRef(true);
  const blockedByPolicy = props.customer.contactPolicy === "DO_NOT_CONTACT";
  const disabled = props.syntheticSmokeMode === true || !props.canAct || !props.coreAvailable || blockedByPolicy || !props.hasConnectedSession || sending;

  useEffect(() => {
    active.current = true;
    return () => { active.current = false; };
  }, []);

  useEffect(() => {
    const textarea = textareaRef.current;
    if (textarea === null) return;
    textarea.style.height = "0px";
    textarea.style.height = `${Math.min(textarea.scrollHeight, 136)}px`;
    textarea.style.overflowY = textarea.scrollHeight > 136 ? "auto" : "hidden";
  }, [props.text]);

  useEffect(() => {
    if (attachment === null) {
      setAttachmentPreview(null);
      return;
    }
    const preview = URL.createObjectURL(attachment);
    setAttachmentPreview(preview);
    return () => { URL.revokeObjectURL(preview); };
  }, [attachment]);

  async function send(): Promise<void> {
    const text = props.text.trim();
    if ((text.length === 0 && attachment === null) || disabled) return;
    setSending(true);
    setError(null);
    setNotice(null);
    try {
      const clientRequestId = requestId();
      const uploaded = attachment === null ? null : await props.api.uploadMedia(attachment);
      const request = await props.api.requestOutboundMessage(props.conversationId, {
        ...(uploaded === null ? {} : { attachmentId: uploaded.id, attachmentKind: uploaded.kind }),
        clientRequestId,
        ...(text.length === 0 ? {} : { text })
      });
      if (active.current) {
        props.onTextChange("");
        setAttachment(null);
      }
      const result = await waitForOutboundResult(props.api, request.id);
      if (!active.current) { await props.onSent(); return; }
      if (result.status === "SENT") {
        setNotice("WhatsApp 已确认投递该消息。");
      } else if (result.status === "NEEDS_HUMAN_REVIEW") {
        setError("WhatsApp 未确认这条消息；为避免重复发送，系统没有自动重发。请先在手机端核对。");
      } else if (result.status === "BLOCKED") {
        setError("消息未发送：当前联系策略或 WhatsApp Session 已阻断该请求。");
      } else {
        setNotice(null);
      }
      await props.onSent();
    } catch (caught) {
      const code = caught instanceof Error ? caught.message : "REQUEST_FAILED";
      if (active.current) setError(errorMessage(code));
    } finally {
      if (active.current) setSending(false);
    }
  }

  return (
    <form aria-label="发送 WhatsApp 消息" className="message-composer" onSubmit={(event) => {
      event.preventDefault();
      void send();
    }}>
      {props.syntheticSmokeMode === true ? <p aria-live="polite" role="status">模拟投递模式：WhatsApp channel 已关闭；此页面不会向客户真实发送消息。</p> : null}
      <button
        aria-label="添加照片或视频"
        className="message-composer__attachment"
        disabled={disabled}
        onClick={() => { attachmentInputRef.current?.click(); }}
        title="添加照片或视频"
        type="button"
      ><AttachmentIcon /></button>
      <input
        accept="image/jpeg,image/png,image/webp,video/mp4"
        aria-label="选择照片或视频"
        className="sr-only"
        onChange={(event) => {
          const file = event.currentTarget.files?.[0] ?? null;
          if (file === null) return;
          const maxBytes = file.type === "video/mp4" ? 32 * 1024 * 1024 : 10 * 1024 * 1024;
          if (!["image/jpeg", "image/png", "image/webp", "video/mp4"].includes(file.type)) { setError("仅支持 JPEG、PNG、WebP 图片或 MP4 视频。"); return; }
          if (file.size > maxBytes) { setError(file.type === "video/mp4" ? "视频不能超过 32MB。" : "图片不能超过 10MB。"); return; }
          setError(null);
          setAttachment(file);
          event.currentTarget.value = "";
        }}
        ref={attachmentInputRef}
        type="file"
      />
      <div className="message-composer__emoji-wrap">
        <button
          aria-expanded={emojiOpen}
          aria-haspopup="dialog"
          aria-label="选择表情"
          className="message-composer__emoji"
          disabled={disabled}
          onClick={() => { setEmojiOpen((open) => !open); }}
          type="button"
        >☺</button>
        {emojiOpen ? <div aria-label="表情选择器" className="message-composer__emoji-picker" role="dialog">
          {QUICK_EMOJIS.map((emoji) => <button
            aria-label={`插入表情 ${emoji}`}
            key={emoji}
            onClick={() => { props.onTextChange(`${props.text}${emoji}`); setEmojiOpen(false); }}
            type="button"
          >{emoji}</button>)}
        </div> : null}
      </div>
      <label className="sr-only" htmlFor="outbound-message">消息</label>
      <textarea
        disabled={disabled}
        id="outbound-message"
        maxLength={4096}
        onChange={(event) => { props.onTextChange(event.currentTarget.value); }}
        onKeyDown={(event) => {
          if (event.key !== "Enter" || event.shiftKey || event.nativeEvent.isComposing) return;
          event.preventDefault();
          void send();
        }}
        placeholder={blockedByPolicy ? "此客户已禁止联系" : "输入消息"}
        ref={textareaRef}
        rows={1}
        value={props.text}
      />
      <div className="message-composer__footer">
        {attachment === null ? null : <div className="message-composer__attachment-name">
          {attachmentPreview === null ? null : attachment.type === "video/mp4"
            ? <video aria-label="待发送视频预览" muted preload="metadata" src={attachmentPreview} />
            : <img alt="待发送图片预览" src={attachmentPreview} />}
          <span>已选择：{attachment.name || (attachment.type === "video/mp4" ? "视频" : "图片")} · {attachment.type} · {(attachment.size / 1024).toLocaleString(undefined, { maximumFractionDigits: 1 })} KB</span>
          <button aria-label="移除附件" onClick={() => { setAttachment(null); }} type="button">×</button>
        </div>}
        <small className="message-composer__hint">
          {!props.coreAvailable ? "核心业务服务暂不可用，已暂停提交；恢复后可重试。" : blockedByPolicy ? "客户联系策略禁止发送。" : !props.hasConnectedSession
            ? "需先保持 WhatsApp Session 已连接。" : "仅在您点击发送后才会通过已绑定的 WhatsApp 发出。"}
        </small>
        <button disabled={disabled || (props.text.trim().length === 0 && attachment === null)} type="submit">
          {sending ? "正在确认…" : "➤"}
        </button>
      </div>
      {notice === null ? null : <p className="message-composer__notice" role="status">{notice}</p>}
      {error === null ? null : <p role="alert">{error}</p>}
    </form>
  );
}

function AttachmentIcon(): React.JSX.Element {
  return <svg aria-hidden="true" fill="none" height="22" viewBox="0 0 24 24" width="22">
    <path d="m20.2 11.1-7.8 7.8a5 5 0 0 1-7.1-7.1l8.5-8.5a3.5 3.5 0 0 1 5 5l-8.6 8.6a2 2 0 0 1-2.8-2.9l7.8-7.8" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.8" />
  </svg>;
}

async function waitForOutboundResult(
  api: ApiClient,
  requestId: string
): Promise<Awaited<ReturnType<ApiClient["getOutboundMessageRequest"]>>> {
  let latest = await api.getOutboundMessageRequest(requestId);
  for (let attempt = 0; attempt < 4; attempt += 1) {
    if (latest.status !== "PENDING" && latest.status !== "SENDING") return latest;
    await delay(1_000);
    latest = await api.getOutboundMessageRequest(requestId);
  }
  return latest;
}

function delay(milliseconds: number): Promise<void> {
  return new Promise((resolve) => { window.setTimeout(resolve, milliseconds); });
}

function requestId(): string {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return "00000000-0000-4000-8000-000000000000".replace(/[018]/g, (digit) =>
    (Number(digit) ^ Math.floor(Math.random() * 16)).toString(16)
  );
}

function errorMessage(code: string): string {
  if (code === "OUTBOUND_CONTACT_BLOCKED") return "客户已设置为禁止联系，消息没有发送。";
  if (code === "OUTBOUND_SESSION_UNAVAILABLE") return "当前 WhatsApp 未连接，消息没有发送。";
  if (code === "FORBIDDEN") return "当前账号没有发送消息的权限。";
  if (code === "OUTBOUND_MEDIA_NOT_FOUND") return "附件不可用，请移除后重新选择。";
  if (code === "MEDIA_INVALID_SIGNATURE" || code === "MEDIA_UNSUPPORTED") return "附件格式无效，请选择有效的图片或 MP4 视频。";
  if (code === "MEDIA_TOO_LARGE") return "附件超过大小限制：图片最多 10 MB，视频最多 32 MB。";
  if (code === "OUTBOUND_REQUEST_CONFLICT") return "发送请求与已提交内容不一致，请先核对原消息状态。";
  return "无法提交消息，请稍后重试。";
}
